import { handleSessionExpired, refreshAccessToken } from '@/lib/api-client'
import { getAccessToken } from '@/lib/auth-session'

export class RequestReplayRequiredError extends Error {
  constructor() {
    super('Your session was refreshed. Please send that request again.')
    this.name = 'RequestReplayRequiredError'
  }
}

export interface AuthenticatedFetchOptions extends RequestInit {
  /** Disable only for requests that may have side effects and lack idempotency protection. */
  replayAfterRefresh?: boolean
}

function withAccessToken(init: RequestInit, token: string | null): RequestInit {
  const headers = new Headers(init.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  else headers.delete('Authorization')
  return { ...init, headers }
}

export async function authenticatedFetch(
  input: RequestInfo | URL,
  options: AuthenticatedFetchOptions = {},
): Promise<Response> {
  const { replayAfterRefresh = true, ...init } = options
  const failedToken = getAccessToken()
  const response = await fetch(input, withAccessToken(init, failedToken))
  if (response.status !== 401) return response

  try {
    const tokens = await refreshAccessToken(failedToken)
    if (!replayAfterRefresh) throw new RequestReplayRequiredError()
    return await fetch(input, withAccessToken(init, tokens.access_token))
  } catch (error) {
    if (error instanceof RequestReplayRequiredError) throw error
    handleSessionExpired()
    throw error
  }
}
